import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Slider,
  LinearProgress,
  IconButton
} from '@mui/material';
import { ArrowForward, PlayArrow, Image, ShoppingCart, Close, CheckCircle, Warning, Error as ErrorIcon, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBook } from '../../Context/BookContext';
import { useAuth } from '../../Context/AuthContext';
import { authenticatedPost } from '../../utils/api';
import { SnackbarState } from '../../types';

const imageGeneratorEndpoint = process.env.REACT_APP_LAMBDA_IMAGE_GENERATOR;

interface PersistentAlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'warning' | 'error';
}

interface GenerateImagesStageProps {
  onNext: () => void;
}

const GenerateImagesStage: React.FC<GenerateImagesStageProps> = ({ onNext }) => {
  const { selectedBook, availableImages, isLoadingCredits, fetchImageCredits, refreshSelectedBook } = useBook();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [isGeneratingImages, setIsGeneratingImages] = useState<boolean>(false);
  const [maxNumImages, setMaxNumImages] = useState<number>(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [persistentAlert, setPersistentAlert] = useState<PersistentAlertState>({ open: false, message: '', severity: 'success' });
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClosePersistentAlert = (): void => {
    setPersistentAlert({ ...persistentAlert, open: false });
  };

  // Set maxNumImages to recommended quantity minus current image count when book is selected
  useEffect(() => {
    if (selectedBook && selectedBook.RecommendedQuantity) {
      const currentImages = selectedBook.ImageCount || 0;
      const remainingRecommended = Math.max(0, selectedBook.RecommendedQuantity - currentImages);
      setMaxNumImages(remainingRecommended);
    }
  }, [selectedBook]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Stop polling function
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Start polling for book status updates
  const startPolling = useCallback(() => {
    stopPolling(); // Clear any existing interval
    
    const pollUpdates = async () => {
      if (!selectedBook) return;
      
      try {
        // Refresh book to get updated status
        const updatedBook = await refreshSelectedBook();
        const bulkStatus = updatedBook?.BulkImageGenerationStatus;
        
        console.log('Polling - BulkImageGenerationStatus:', bulkStatus);
        
        // Handle completed status - stop polling and show success
        if (bulkStatus === 'completed') {
          console.log('Bulk image generation completed, stopping polling');
          setIsGeneratingImages(false);
          stopPolling();
          
          // Refresh credits
          fetchImageCredits();
          
          setPersistentAlert({
            open: true,
            message: '✅ All images generated successfully! Your images are ready to view.',
            severity: 'success'
          });
          return;
        }
        
        // Handle partial status - some images succeeded
        if (bulkStatus === 'partial') {
          console.log('Bulk image generation partially completed');
          setIsGeneratingImages(false);
          stopPolling();
          
          // Refresh credits
          fetchImageCredits();
          
          setPersistentAlert({
            open: true,
            message: '✅ Some images generated successfully, but others failed. Credits for failed images have been refunded to your account.',
            severity: 'success'
          });
          return;
        }
        
        // Handle error status - stop polling and show error
        if (bulkStatus === 'error') {
          console.log('Bulk image generation failed');
          setIsGeneratingImages(false);
          stopPolling();
          
          // Refresh credits
          fetchImageCredits();
          
          setPersistentAlert({
            open: true,
            message: '❌ Image generation failed. All credits have been refunded to your account.',
            severity: 'error'
          });
          return;
        }
        
        // Still generating - poll credits
        if (bulkStatus === 'generating') {
          setIsGeneratingImages(true);
          fetchImageCredits();
        }
        
      } catch (error) {
        console.error('Error polling for updates:', error);
      }
    };

    // Poll immediately, then every 3 seconds
    pollUpdates();
    pollingIntervalRef.current = setInterval(pollUpdates, 3000);
  }, [selectedBook, refreshSelectedBook, fetchImageCredits, stopPolling]);

  // Resume polling if book is in generating state (e.g. user refreshed page during generation)
  useEffect(() => {
    if (selectedBook?.BulkImageGenerationStatus === 'generating' && !pollingIntervalRef.current) {
      console.log('Resuming polling for generating bulk images');
      setIsGeneratingImages(true);
      startPolling();
    }
  }, [selectedBook?.FileId, selectedBook?.BulkImageGenerationStatus, startPolling]);

  const handleGenerateImages = async (): Promise<void> => {
    if (!isAuthenticated || !user?.userId) {
      setSnackbar({
        open: true,
        message: 'User not authenticated',
        severity: 'error'
      });
      return;
    }

    if (!selectedBook) {
      setSnackbar({
        open: true,
        message: 'Please select a book first',
        severity: 'error'
      });
      return;
    }

    if (!selectedBook.WordCount) {
      setSnackbar({
        open: true,
        message: 'Book is still being processed. Please wait for processing to complete.',
        severity: 'warning'
      });
      return;
    }

    if (availableImages !== null && availableImages === 0) {
      setSnackbar({
        open: true,
        message: 'You need at least 1 credit to generate images. Please add credits first.',
        severity: 'error'
      });
      return;
    }

    if (!imageGeneratorEndpoint) {
      setSnackbar({
        open: true,
        message: 'Image generation endpoint not configured.',
        severity: 'error'
      });
      return;
    }

    if (availableImages !== null && maxNumImages > availableImages) {
      setSnackbar({
        open: true,
        message: `You want to generate ${maxNumImages} images but only have ${availableImages} credits. Please add more credits or reduce the number of images.`,
        severity: 'error'
      });
      return;
    }

    setIsGeneratingImages(true);

    try {
      const response = await authenticatedPost(imageGeneratorEndpoint!, {
        FileId: selectedBook.FileId,
        MaxNumImages: maxNumImages
      });
      
      console.log('Image generation response:', response.data);
      setSnackbar({
        open: true,
        message: 'Image generation started! Tracking progress...',
        severity: 'info'
      });
      
      // Start polling for status updates
      startPolling();
      
    } catch (error: any) {
      console.error('Error generating images:', error);
      
      let errorMessage = 'Failed to generate images. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        if (response && response.data && response.data.message) {
          errorMessage = response.data.message;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setIsGeneratingImages(false);
    }
  };

  const handleMaxNumImagesChange = (value: number): void => {
    setMaxNumImages(value);
  };

  const isBookReady = selectedBook?.ProcessingStatus === 'completed';

  return (
    <Box>
      {/* Persistent Alert for Completion Status */}
      {persistentAlert.open && (
        <Alert 
          severity={persistentAlert.severity}
          icon={
            persistentAlert.severity === 'success' ? <CheckCircle sx={{ fontSize: 28 }} /> :
            persistentAlert.severity === 'warning' ? <Warning sx={{ fontSize: 28 }} /> :
            <ErrorIcon sx={{ fontSize: 28 }} />
          }
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClosePersistentAlert}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
          sx={{ 
            mb: 3,
            p: 3,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 2,
            border: '2px solid',
            borderColor: 
              persistentAlert.severity === 'success' ? 'success.main' :
              persistentAlert.severity === 'warning' ? 'warning.main' :
              'error.main',
            backgroundColor: 
              persistentAlert.severity === 'success' ? 'rgba(76, 175, 80, 0.1)' :
              persistentAlert.severity === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
              'rgba(244, 67, 54, 0.1)',
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          {persistentAlert.message}
        </Alert>
      )}

      {!isBookReady ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the upload stage first. Your book needs to finish processing before you can generate images.
        </Alert>
      ) : null}

      {/* Processing Status Indicator */}
      {isGeneratingImages && (
        <Box sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
          <Alert 
            severity="info" 
            icon={<Image sx={{ fontSize: 16 }} />}
            sx={{ 
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderLeft: `4px solid`,
              borderColor: 'primary.main',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Generating images...
              </Typography>
              <CircularProgress size={16} />
            </Box>
          </Alert>
          <LinearProgress sx={{ mt: 1 }} />
        </Box>
      )}

      <Card sx={{ 
        border: '2px solid',
        borderColor: isBookReady ? 'secondary.main' : 'divider',
        mb: 4
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Image sx={{ fontSize: 48, color: isBookReady ? 'secondary.main' : 'text.disabled' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                Generate Images for Your Book
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selectedBook 
                  ? `Create AI-generated illustrations for "${selectedBook.Title || selectedBook.FileName}"` 
                  : 'Select a book to generate images'
                }
              </Typography>
            </Box>
          </Box>

          {selectedBook && (
            <>
              {/* Recommendation Info Box */}
              {selectedBook.RecommendedQuantity && (
                <Box sx={{ 
                  p: 3, 
                  mb: 3,
                  backgroundColor: 'rgba(116, 164, 137, 0.1)', 
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📊 Recommended: {selectedBook.RecommendedQuantity} images for full book coverage
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                      ✅ <strong>Already generated:</strong> {selectedBook.ImageCount || 0} {(selectedBook.ImageCount || 0) === 1 ? 'image' : 'images'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                      📝 <strong>Remaining:</strong> {Math.max(0, selectedBook.RecommendedQuantity - (selectedBook.ImageCount || 0))} {Math.max(0, selectedBook.RecommendedQuantity - (selectedBook.ImageCount || 0)) === 1 ? 'image' : 'images'}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    You can generate fewer images if you prefer.
                  </Typography>
                </Box>
              )}

              {/* Credits Available */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2, 
                mb: 3,
                backgroundColor: 'action.hover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Available credits: <strong>{isLoadingCredits ? '...' : availableImages !== null ? availableImages : 'N/A'}</strong>
                </Typography>
                
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ShoppingCart />}
                  onClick={() => navigate('/credits')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'rgba(116, 164, 137, 0.08)'
                    }
                  }}
                >
                  Buy More Credits
                </Button>
              </Box>

              {/* Number of Images Input with Slider */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'text.secondary', fontWeight: 500 }}>
                  How many images would you like to generate?
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    type="number"
                    value={maxNumImages}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const remainingImages = Math.max(0, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0));
                      // Enforce max limit to remaining images
                      handleMaxNumImagesChange(Math.min(value, remainingImages));
                    }}
                    onBlur={() => {
                      const remainingImages = Math.max(0, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0));
                      if (maxNumImages < 0) {
                        handleMaxNumImagesChange(0);
                      } else if (maxNumImages > remainingImages) {
                        handleMaxNumImagesChange(remainingImages);
                      }
                    }}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: Math.max(0, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0)),
                        step: 1,
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{ 
                      width: '100px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'action.hover',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        }
                      }
                    }}
                    size="small"
                    disabled={!isBookReady}
                  />
                </Box>

                <Slider
                  value={maxNumImages}
                  onChange={(_, value) => handleMaxNumImagesChange(value as number)}
                  min={0}
                  max={Math.max(1, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0))}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { 
                      value: Math.max(1, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0)), 
                      label: Math.max(1, (selectedBook.RecommendedQuantity || 0) - (selectedBook.ImageCount || 0)).toString() 
                    }
                  ]}
                  sx={{ 
                    mb: 2,
                    '& .MuiSlider-track': {
                      backgroundColor: 'primary.main'
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'action.disabled'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }
                  }}
                  disabled={!isBookReady}
                />

                {/* Helper Text */}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    textAlign: 'center',
                    color: availableImages !== null && maxNumImages > availableImages ? 'error.main' : 'text.secondary',
                    fontWeight: availableImages !== null && maxNumImages > availableImages ? 600 : 400
                  }}
                >
                  {availableImages !== null && maxNumImages > availableImages ? 
                    `⚠️ You only have ${availableImages} credits available` : 
                    selectedBook?.RecommendedQuantity && (selectedBook.ImageCount || 0) >= selectedBook.RecommendedQuantity ?
                      '✅ All recommended images have been generated' : 
                      `Each image costs 1 credit (Total: ${maxNumImages} ${maxNumImages === 1 ? 'credit' : 'credits'})`
                  }
                </Typography>
              </Box>

              {/* Generate Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={isGeneratingImages ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={handleGenerateImages}
                disabled={isGeneratingImages || isLoadingCredits || !isBookReady || maxNumImages === 0}
                fullWidth
                sx={{
                  backgroundColor: 'secondary.main',
                  '&:hover': { 
                    backgroundColor: 'secondary.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(156, 39, 176, 0.3)'
                  },
                  '&:disabled': {
                    background: 'action.disabled'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 2,
                  fontSize: '1rem'
                }}
              >
                {isGeneratingImages ? 'Generating Images...' : `Generate ${maxNumImages} Image${maxNumImages !== 1 ? 's' : ''}`}
              </Button>

              {/* Info Message */}
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  Generated images will be automatically added to your book. 
                  You can view them on the Images page once they're ready.
                </Typography>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>

      {/* How This Works Info Box */}
      <Card sx={{ 
        mb: 4,
        border: '2px solid',
        borderColor: 'info.main',
        backgroundColor: 'info.light'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Info sx={{ color: 'info.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              How This Works
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            This is where you generate the images that will be added to your book. 
            Our system recommends a specific number of images based on your book's length—approximately one image every 1,000 words, or every 2-4 pages. 
            Images are generated sequentially from the beginning of your book to the end, so if you generate 50 out of 100 recommended images, the first half of your book will be illustrated. 
            You can always come back later to generate the remaining images, and your previously generated images will still be there—already paid for and integrated. 
            Each image is placed exactly where its subject is mentioned or described in the text, creating a seamless visual reading experience. 
            If you're unhappy with any generated image, you can delete it from your book's images page, and it will be regenerated when you generate new images. 
            The images are automatically integrated into your EPUB file, so when you download your book, everything is ready to read. 
            Each generated image costs 1 credit, and you can see your available credits displayed above.
          </Typography>
        </CardContent>
      </Card>

      {/* Next Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={onNext}
          disabled={!selectedBook}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': { 
              backgroundColor: 'primary.dark',
              transform: 'translateY(-2px)'
            },
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            px: 4
          }}
        >
          Next: Download / Read
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerateImagesStage;

